import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listMyCoupons, type MyCoupon } from "@/lib/my-coupons.functions";
import { formatPrice } from "@/lib/format";
import { PENDING_COUPON_KEY } from "@/hooks/storefront/use-auto-apply-coupon";

export const Route = createFileRoute("/_authenticated/account/coupons")({
  head: () => ({
    meta: [
      { property: "og:url", content: "https://mrpanda.gr/account/coupons" },
      { title: "Τα κουπόνια μου — MrPanda.gr" },
      {
        name: "description",
        content: "Δες τα κουπόνια σου, μαζί με όσα κέρδισες στα παιχνίδια του MrPanda.",
      },
    ],
  }),
  component: CouponsTab,
});

function discountLabel(c: MyCoupon) {
  return c.discountType === "percentage"
    ? `${c.discountValue}% έκπτωση`
    : `${formatPrice(c.discountValue)} έκπτωση`;
}

function CouponCard({ c }: { c: MyCoupon }) {
  const active = c.status === "active";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(c.code);
      toast.success("Ο κωδικός αντιγράφηκε! 🐼");
    } catch {
      toast.error("Δεν έγινε η αντιγραφή");
    }
  };

  return (
    <li
      className={`relative overflow-hidden rounded-2xl border p-4 transition ${
        active
          ? "border-primary/40 bg-primary/5 shadow-sm hover:shadow-md"
          : "border-border bg-muted/40 opacity-80"
      }`}
    >
      <span className="absolute -left-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-background" />
      <span className="absolute -right-3 top-1/2 h-6 w-6 -translate-y-1/2 rounded-full bg-background" />
      <div className="flex items-start gap-3">
        <span className="text-2xl">{c.source === "game" ? "🎮" : "🏷️"}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <code className="rounded-lg border border-dashed border-border bg-background px-2 py-1 text-sm font-bold tracking-wider">
              {c.code}
            </code>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
              {discountLabel(c)}
            </span>
            {c.status === "used" && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs">Χρησιμοποιήθηκε</span>
            )}
            {c.status === "expired" && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-xs">Έληξε</span>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {c.source === "game" ? "Κερδισμένο στο παιχνίδι 🐼" : "Κουπόνι καταστήματος"}
            {" • ισχύει μόνο για παραγγελίες μέσω eshop"}
            {c.minCartValue > 0 && ` • ελάχιστο καλάθι ${formatPrice(c.minCartValue)}`}
            {c.expiresAt &&
              c.status === "active" &&
              ` • ισχύει έως ${new Date(c.expiresAt).toLocaleDateString("el-GR")}`}
            {c.usedAt && ` • ${new Date(c.usedAt).toLocaleDateString("el-GR")}`}
          </p>
          {active && (
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              <button
                onClick={copy}
                className="rounded-full border border-border px-3 py-1.5 transition hover:bg-muted"
              >
                📋 Αντιγραφή
              </button>
              <Link
                to="/cart"
                onClick={() => {
                  try {
                    window.localStorage.setItem(PENDING_COUPON_KEY, c.code.toUpperCase());
                  } catch {
                    /* ignore */
                  }
                }}
                className="rounded-full bg-primary px-3 py-1.5 font-medium text-primary-foreground transition hover:bg-primary/90"
              >
                Χρήση τώρα
              </Link>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

function CouponsTab() {
  const fetchCoupons = useServerFn(listMyCoupons);
  const { data, isLoading } = useQuery({
    queryKey: ["my-coupons"],
    queryFn: () => fetchCoupons(),
  });

  const coupons = data ?? [];
  const active = coupons.filter((c) => c.status === "active");
  const past = coupons.filter((c) => c.status !== "active");

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
      <h2 className="mb-4 flex items-center gap-2 text-xl font-bold">🎟️ Τα κουπόνια μου</h2>

      {isLoading ? (
        <div className="space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl bg-muted/60" />
          ))}
        </div>
      ) : coupons.length === 0 ? (
        <div className="rounded-xl bg-muted/50 p-10 text-center">
          <div className="mb-2 text-4xl">🎁</div>
          <p className="text-sm text-muted-foreground">
            Δεν έχεις κουπόνια ακόμη. Παίξε το παιχνίδι του Panda και κέρδισε έκπτωση!
          </p>
          <Link to="/" className="mt-3 inline-block text-sm font-medium text-primary underline">
            Πάμε στην αρχική
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {active.length > 0 && (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Ενεργά</h3>
              <ul className="grid gap-3 sm:grid-cols-2">
                {active.map((c) => (
                  <CouponCard key={`a-${c.code}`} c={c} />
                ))}
              </ul>
            </section>
          )}
          {past.length > 0 && (
            <section>
              <h3 className="mb-2 text-sm font-semibold text-muted-foreground">Ιστορικό</h3>
              <ul className="grid gap-3 sm:grid-cols-2">
                {past.map((c, i) => (
                  <CouponCard key={`p-${c.code}-${i}`} c={c} />
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
